/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import validator from 'validator';
import { ImageIcon, Pills } from '@saleshandy/design-system';
import { ChevronDown } from '@saleshandy/icons';
import { Spinner } from 'react-bootstrap';
import { accessibleOnClick } from '../../../../../../../../../shared/utils/accessible-on-click';
import { EmailListFromOpt } from '../utils/helper';
import { Google } from '../../../../../../../../../shared/svg/google';
import { Images } from '../../../../../../../../../shared/app-constants';
import {
  EmailAccountMinimalListResponse,
  getMinimalEmailAccountListProps,
} from '../../../../../../../types';
import { Dot } from '../../../../../../../../../shared/svg/dot';
import { OverlayTooltip } from '../../../../../../../../../shared/design-system/components/overlay';

type EmailDropdownInputType = {
  name: string;
  label: string;
  value: string;
  emails: string[] | EmailListFromOpt[];
  onChange: (name: string, value: string[], selectedEmailId?: number) => void;
  onValueChange: (name: string, value: string) => void;
  isEmailsRemovable?: boolean;
  nonRemovableEmails?: string[];
  options?: EmailListFromOpt[];
  getEmailAccounts?: (filter: getMinimalEmailAccountListProps) => void;
  emailAccount?: EmailAccountMinimalListResponse;
};

const EmailDropdownInput: React.FC<EmailDropdownInputType> = ({
  name = '',
  label = '',
  value = '',
  emails = [],
  onChange,
  onValueChange,
  isEmailsRemovable = false,
  nonRemovableEmails = [],
  options = [],
  getEmailAccounts,
  emailAccount,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isLoadingMoreRef = useRef(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [allOptions, setAllOptions] = useState<EmailListFromOpt[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Pagination state for email accounts
  const [skip, setSkip] = useState(0);
  const take = 25; // Page size
  const hasMore = emailAccount?.hasMore || false;

  // Callback ref to assign both local ref and passed ref
  const setDropdownRef = useCallback((element: HTMLDivElement | null) => {
    dropdownRef.current = element;
    scrollContainerRef.current = element;
  }, []);

  // Track if we've initialized from options prop
  const hasInitializedRef = useRef(false);

  // Initialize options from props (only once, for initial page)
  useEffect(() => {
    if (options && options.length > 0 && !hasInitializedRef.current) {
      setAllOptions(options);
      hasInitializedRef.current = true;
    }
  }, [options]);

  // Merge email accounts from API pagination (for subsequent pages)
  useEffect(() => {
    if (
      emailAccount?.emailAccounts &&
      emailAccount.emailAccounts.length > 0 &&
      skip > 0
    ) {
      // Only process if this is a pagination request (skip > 0)
      // The first page is already handled by options prop
      const newEmailAccounts: EmailListFromOpt[] = emailAccount?.emailAccounts?.map(
        (account) => ({
          id: account?.id,
          name: account?.fromName,
          email: account?.fromEmail,
          status: account?.status,
          emailServiceProvider: account?.emailServiceProvider,
        }),
      );

      // Merge with existing options, avoiding duplicates
      setAllOptions((prev) => {
        const existingIds = new Set(prev.map((opt) => opt.id));
        const uniqueNewAccounts = newEmailAccounts.filter(
          (acc) => !existingIds.has(acc.id),
        );

        if (uniqueNewAccounts.length > 0) {
          return [...prev, ...uniqueNewAccounts];
        }
        return prev;
      });
    }
  }, [emailAccount, skip]);
  // Filter options based on input value
  const filteredOptions = useMemo(() => {
    if (value.trim()) {
      return allOptions.filter((option) =>
        option?.email?.toLowerCase().includes(value.toLowerCase()),
      );
    }
    return allOptions;
  }, [value, allOptions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Load more email accounts
  const handleLoadMore = useCallback(() => {
    if (!getEmailAccounts || isLoadingMoreRef.current || !hasMore) {
      return;
    }

    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    const nextSkip = skip + take;
    setSkip(nextSkip);

    getEmailAccounts({
      skip: nextSkip,
      take,
    });
  }, [getEmailAccounts, skip, take, hasMore]);

  // Reset loading ref when new data arrives or request fails
  useEffect(() => {
    if (emailAccount?.emailAccounts !== undefined) {
      // Small delay to prevent rapid successive calls
      const timer = setTimeout(() => {
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [emailAccount]);

  // Infinite scroll handler for dropdown
  const handleDropdownScroll = useCallback(() => {
    const container = dropdownRef.current;
    if (!container || !getEmailAccounts) return;

    // Prevent duplicate calls
    if (isLoadingMoreRef.current) return;

    // Don't load if there are no more items
    if (!hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    // Load more when the user is 50px from the bottom
    const threshold = 50;
    const isNearBottom = scrollHeight - scrollTop <= clientHeight + threshold;

    if (isNearBottom && hasMore) {
      handleLoadMore();
    }
  }, [getEmailAccounts, hasMore, handleLoadMore]);

  // Attach scroll listener to dropdown when it's visible
  useEffect(() => {
    if (!showDropdown || !getEmailAccounts) return;

    const container = dropdownRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleDropdownScroll, {
      passive: true,
    });
    // eslint-disable-next-line consistent-return
    return () => {
      container.removeEventListener('scroll', handleDropdownScroll);
    };
  }, [showDropdown, handleDropdownScroll, getEmailAccounts]);

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onValueChange(name, event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const trimmedValue = value.trim();
      if (validator.isEmail(trimmedValue)) {
        onChange(name, [trimmedValue]);
        onValueChange(name, '');
        setShowDropdown(false);
      }
    } else if (event.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleChevronClick = () => {
    setShowDropdown(!showDropdown);
  };

  const getEmailString = (email: string | EmailListFromOpt): string => {
    if (typeof email === 'string') {
      return email;
    }
    return email?.email;
  };

  const handleSelectOption = (option: EmailListFromOpt) => {
    // Prevent selection if status === 0 (inactive/disabled)
    if (option.status === 0) {
      return;
    }
    const emailString = option.email.trim();
    if (validator.isEmail(emailString)) {
      onChange(name, [emailString], option?.id);
      onValueChange(name, '');
      setShowDropdown(false);
      inputRef.current?.focus();
    }
  };

  const onEmailRemove = (email: string | EmailListFromOpt) => {
    onChange(name, []);
  };

  // Normalize emails to always be an array
  const normalizedEmails = useMemo(() => {
    if (!emails) return [];
    if (typeof emails === 'string') return [emails];
    if (Array.isArray(emails)) return emails;
    return [];
  }, [emails]);

  return (
    <div className="reply-forward-editor--input" ref={containerRef}>
      <span className="label">{label}</span>
      <div className="email-pills-input-wrapper">
        <div className="email-pills-container">
          {normalizedEmails.map((email) => {
            const emailString = getEmailString(email);
            return (
              email && (
                <Pills
                  theme="outline"
                  size="md"
                  label={emailString}
                  showCloseIcon={
                    isEmailsRemovable &&
                    !nonRemovableEmails.includes(emailString)
                  }
                  onClose={() => onEmailRemove(email)}
                  key={emailString}
                />
              )
            );
          })}
          <div className="email-input-with-chevron">
            <input
              ref={inputRef}
              className="email-input"
              value={value}
              onChange={handleOnChange}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                // Open dropdown when input is focused and has filtered options
                if (filteredOptions.length > 0) {
                  setShowDropdown(true);
                }
              }}
            />
            <div
              className="chevron-icon"
              {...accessibleOnClick(handleChevronClick)}
            >
              <ChevronDown />
            </div>
          </div>
        </div>
        {showDropdown && filteredOptions.length > 0 && (
          <div className="email-dropdown" ref={setDropdownRef}>
            {filteredOptions?.map((option) => {
              const isDisabled = option?.status === 0;
              const isActive = option?.status === 1;
              const dotClassName = isActive
                ? 'email-dropdown-dot--active'
                : 'email-dropdown-dot--inactive';
              return (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <OverlayTooltip
                  key={option.id}
                  text="Email account is disconnected. Please reconnect it to send emails."
                  className={!isDisabled ? 'email-disabled-tooltip' : ''}
                >
                  <div
                    key={option.id}
                    className={`email-dropdown-option ${
                      isDisabled ? 'email-dropdown-option--disabled' : ''
                    }`}
                    {...(isDisabled
                      ? {}
                      : accessibleOnClick(() => handleSelectOption(option)))}
                    onMouseDown={(e) => {
                      if (isDisabled) {
                        e.preventDefault();
                        return;
                      }
                      e.preventDefault();
                      handleSelectOption(option);
                    }}
                  >
                    <span className="email-dropdown-option-name">
                      {option?.name}{' '}
                      {['gmail', 'gsuite']?.includes(
                        option?.emailServiceProvider,
                      ) && <Google />}
                      {option?.emailServiceProvider === 'outlook' && (
                        <ImageIcon
                          src={Images.Outlook}
                          alt="outlook-icon"
                          width={16}
                          height={16}
                        />
                      )}
                      {option?.emailServiceProvider === 'yahoo' && (
                        <ImageIcon
                          src={Images.Yahoo}
                          alt="yahoo-mail-icon"
                          width={16}
                          height={16}
                        />
                      )}
                      {option?.emailServiceProvider === 'zoho' && (
                        <ImageIcon
                          src={Images.Zoho}
                          alt="zoho-mail-icon"
                          width={16}
                          height={16}
                        />
                      )}
                      {option?.emailServiceProvider === 'microsoft' && (
                        <ImageIcon
                          src={Images.Microsoft}
                          alt="microsoft-365-icon"
                          width={16}
                          height={16}
                        />
                      )}
                    </span>
                    <span className="email-dropdown-option-email">
                      {option?.email}
                      <Dot className={dotClassName} />
                    </span>
                  </div>
                </OverlayTooltip>
              );
            })}
            {isLoadingMore && (
              <div className="email-dropdown-loading">
                <Spinner className="spinner--blue" animation="border" />
              </div>
            )}
            {!hasMore && filteredOptions && filteredOptions.length > 15 && (
              <div className="email-dropdown-end-divider">
                <span>The End</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailDropdownInput;
